import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, Download, MoreHorizontal, Upload, History } from 'lucide-react';
import { FundoForm, Classe, DocumentoAnexo, DocumentoVersao } from './types';
import {
  Opt, FORMA_JURIDICA, MOEDA, JUROS, TIPO_COTA, CONDOMINIO, STATUS_APLICACAO,
  BENCHMARK, TIPO_CONTA, PAPEL, DOCS_OBRIGATORIOS,
} from './mock';

const v = (x: any): string => (x !== undefined && x !== null && String(x).trim() !== '' ? String(x) : '—');
const sn = (b: boolean): string => (b ? 'Sim' : 'Não');
const money = (x?: string): string => (x && x.trim() ? `R$ ${x}` : '—');
const pct = (x?: string): string => (x && x.trim() ? `${x}%` : '—');
const lbl = (opts: Opt[], value?: string): string => opts.find((o) => o.value === value)?.label || v(value);

const Item: React.FC<{ k: string; val: React.ReactNode }> = ({ k, val }) => (
  <div className="fl-rev-item">
    <span className="k">{k}</span>
    <span className="val">{val}</span>
  </div>
);

const Secao: React.FC<{ titulo: string; children: React.ReactNode }> = ({ titulo, children }) => (
  <div className="fl-rev-section">
    <div className="fl-section-title">{titulo}</div>
    {children}
  </div>
);

// Detalhe completo de uma classe (mesmo formato de sempre) — usado na linha expandida.
const ClasseDetalhe: React.FC<{ c: Classe }> = ({ c }) => (
  <>
    <div className="fl-rev-sub">Identificação</div>
    <div className="fl-rev-grid">
      <Item k="Subclasse" val={v(c.subclasse)} />
      <Item k="CNPJ da classe" val={v(c.cnpj_classe)} />
      <Item k="Nome" val={v(c.nome)} />
      <Item k="Tipo de cota" val={lbl(TIPO_COTA, c.tipo_cota)} />
      <Item k="Condomínio" val={lbl(CONDOMINIO, c.condominio)} />
      <Item k="Status" val={lbl(STATUS_APLICACAO, c.status_aplicacao)} />
      <Item k="Público-alvo" val={v(c.publico_alvo)} />
      <Item k="Calendário" val={v(c.calendario)} />
    </div>

    <div className="fl-rev-sub">Mercado</div>
    <div className="fl-rev-grid">
      <Item k="CETIP" val={v(c.cod_cetip)} />
      <Item k="ISIN" val={v(c.isin)} />
      <Item k="Benchmark" val={lbl(BENCHMARK, c.benchmark)} />
      <Item k="Spread" val={pct(c.spread)} />
      <Item k="Rating" val={v(c.rating)} />
      <Item k="Razão de garantia" val={pct(c.razao_garantia)} />
      <Item k="Razão garantia (target)" val={pct(c.razao_garantia_target)} />
    </div>

    <div className="fl-rev-sub">Condições</div>
    <div className="fl-rev-grid">
      <Item k="Tx. administração" val={pct(c.condicoes.tx_adm)} />
      <Item k="Tx. performance" val={pct(c.condicoes.tx_performance)} />
      <Item k="Tx. custódia" val={pct(c.condicoes.tx_custodia)} />
      <Item k="Tx. adm. fiduciária" val={pct(c.condicoes.tx_adm_fiduciaria)} />
      <Item k="Tx. adm. mínima" val={money(c.condicoes.tx_adm_minima)} />
      <Item k="Mín. adm. fiduciário" val={money(c.condicoes.valor_minimo_adm_fiduciario)} />
      <Item k="Valor 1ª cota" val={money(c.condicoes.vlr_primeira_cota)} />
      <Item k="Aplicação inicial" val={money(c.condicoes.aplicacao_inicial)} />
      <Item k="Aplicação mínima" val={money(c.condicoes.aplicacao_minima)} />
      <Item k="Saldo mínimo" val={money(c.condicoes.saldo_minimo)} />
      <Item k="Resgate mínimo" val={money(c.condicoes.resgate_minimo)} />
      <Item k="Cotização aplicação" val={v(c.condicoes.d_cotizacao_aplic)} />
      <Item k="Cotização resgate" val={v(c.condicoes.d_cotizacao_resgate)} />
      <Item k="Pagamento resgate" val={v(c.condicoes.d_pgto_resgate)} />
      <Item k="Taxa de saída" val={sn(c.condicoes.taxa_de_saida)} />
      <Item k="Taxa de saída (valor)" val={money(c.condicoes.taxa_saida_valor)} />
      <Item k="Vigente desde" val={v(c.condicoes.vigente_desde)} />
    </div>

    <div className="fl-rev-sub">Flags</div>
    <div className="fl-rev-grid">
      <Item k="Ativa" val={sn(c.is_active)} />
      <Item k="Divulga no site" val={sn(c.divulgacao_site)} />
      <Item k="Início" val={v(c.dt_inicio)} />
      <Item k="Encerramento" val={v(c.dt_encerramento)} />
    </div>
  </>
);

// Tabela de classes: linha-resumo clicável que expande o detalhe completo.
const ClassesTabela: React.FC<{ classes: Classe[] }> = ({ classes }) => {
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const toggle = (k: string) =>
    setAbertas((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });

  if (classes.length === 0) return <div className="fl-empty">Nenhuma classe.</div>;

  return (
    <table className="fl-table fl-table--expand">
      <thead>
        <tr>
          <th className="fl-exp-col" />
          <th>Subclasse</th><th>Nome</th><th>Benchmark</th><th>Tx. adm.</th><th>Status</th><th>Situação</th>
        </tr>
      </thead>
      <tbody>
        {classes.map((c) => {
          const aberta = abertas.has(c._key);
          return (
            <React.Fragment key={c._key}>
              <tr className={`fl-row-click${aberta ? ' is-open' : ''}`} onClick={() => toggle(c._key)}>
                <td className="fl-exp-col">
                  <ChevronRight size={16} className={`fl-exp-caret${aberta ? ' open' : ''}`} />
                </td>
                <td>{v(c.subclasse)}</td>
                <td>{v(c.nome)}</td>
                <td>{lbl(BENCHMARK, c.benchmark)}</td>
                <td>{pct(c.condicoes.tx_adm)}</td>
                <td>{lbl(STATUS_APLICACAO, c.status_aplicacao)}</td>
                <td>
                  <span className={`fl-badge ${c.is_active ? 'fl-badge--on' : 'fl-badge--off'}`}>
                    {c.is_active ? 'Ativa' : 'Inativa'}
                  </span>
                </td>
              </tr>
              {aberta && (
                <tr className="fl-exp-row">
                  <td colSpan={7}>
                    <div className="fl-exp-detail"><ClasseDetalhe c={c} /></div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

// Menu de ações de um documento (⋯): baixar, substituir, ver versões (modal).
const DocActions: React.FC<{
  anexo: DocumentoAnexo;
  temArquivo: boolean;
  onDownload?: (a: DocumentoAnexo, v?: DocumentoVersao) => void;
  onSubstituir?: (a: DocumentoAnexo) => void;
}> = ({ anexo, temArquivo, onDownload, onSubstituir }) => {
  const [menu, setMenu] = useState(false);
  const [modal, setModal] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const MENU_W = 220;
  const abrir = () => setMenu(true);

  // posiciona o menu medindo a altura real: abre pra baixo, ou pra cima se faltar espaço.
  // useLayoutEffect roda antes do paint → sem "flash" na posição errada.
  useLayoutEffect(() => {
    if (!menu) return;
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const m = 8; // margem mínima da borda da tela
    const h = menuRef.current?.offsetHeight ?? 150;
    const w = menuRef.current?.offsetWidth ?? MENU_W;
    const abreCima = r.bottom + h + m > window.innerHeight && r.top - h - m > 0;
    const top = abreCima ? r.top - h - 4 : r.bottom + 4;
    const left = Math.min(Math.max(m, r.right - w), window.innerWidth - w - m);
    setPos({
      top: Math.max(m, Math.min(top, window.innerHeight - h - m)),
      left,
    });
  }, [menu]);

  // fecha em clique fora / scroll / resize (menu vive num portal, fora do overflow da tela)
  useEffect(() => {
    if (!menu) return;
    const fora = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setMenu(false);
    };
    const fechar = () => setMenu(false);
    document.addEventListener('mousedown', fora);
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    return () => {
      document.removeEventListener('mousedown', fora);
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
    };
  }, [menu]);

  const versoes = anexo.versoes ?? [];

  return (
    <div className="fl-doc-actions">
      <button
        ref={triggerRef}
        type="button" className="fl-doc-trigger" onClick={() => (menu ? setMenu(false) : abrir())}
        title="Ações" aria-haspopup="menu" aria-expanded={menu}
      >
        <MoreHorizontal size={18} />
      </button>

      {menu && createPortal(
        <ul ref={menuRef} className="fl-doc-menu fl-doc-menu--fixed" role="menu" style={{ top: pos.top, left: pos.left }}>
          <li>
            <button type="button" role="menuitem" disabled={!temArquivo}
              onClick={() => { setMenu(false); onDownload?.(anexo); }}>
              <Download size={15} /> Baixar arquivo
            </button>
          </li>
          <li>
            <button type="button" role="menuitem"
              onClick={() => { setMenu(false); onSubstituir?.(anexo); }}>
              <Upload size={15} /> {temArquivo ? 'Substituir' : 'Anexar'}
            </button>
          </li>
          <li>
            <button type="button" role="menuitem" disabled={versoes.length === 0}
              onClick={() => { setMenu(false); setModal(true); }}>
              <History size={15} /> Versões{versoes.length ? ` (${versoes.length})` : ''}
            </button>
          </li>
        </ul>,
        document.body,
      )}

      {modal && (
        <div className="fl-overlay" onMouseDown={() => setModal(false)}>
          <div className="fl-modal" onMouseDown={e => e.stopPropagation()}>
            <div className="fl-modal-header">
              <h3>Versões — {anexo.titulo ?? anexo.tipo}</h3>
              <button className="fl-icon-btn" onClick={() => setModal(false)} aria-label="Fechar">✕</button>
            </div>
            <div className="fl-modal-body">
              {versoes.length === 0 ? (
                <div className="fl-empty">Sem versões registradas.</div>
              ) : (
                <table className="fl-table">
                  <thead><tr><th>Versão</th><th>Arquivo</th><th>Data</th><th>Tamanho</th><th>Atual</th><th className="fl-col-acoes" /></tr></thead>
                  <tbody>
                    {versoes.map(ver => (
                      <tr key={ver.numero_versao}>
                        <td>v{ver.numero_versao}</td>
                        <td>{ver.nome_arquivo}</td>
                        <td>{ver.data_referencia ?? '—'}</td>
                        <td>{ver.tamanho ?? '—'}</td>
                        <td>{ver.is_current ? <span className="fl-badge fl-badge--on">Atual</span> : '—'}</td>
                        <td className="fl-col-acoes">
                          <button type="button" className="fl-doc-download" onClick={() => onDownload?.(anexo, ver)} title="Baixar versão">
                            <Download size={14} /> Baixar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Seções dos campos 1:1 do fundo (reutilizadas no resumo e como base do modo de edição).
export const ResumoDadosFundo: React.FC<{ form: FundoForm }> = ({ form }) => (
  <>
    <Secao titulo="Identificação">
      <div className="fl-rev-grid">
        <Item k="CNPJ" val={v(form.cnpj)} />
        <Item k="Razão social" val={v(form.razao_social)} />
        <Item k="Nome fantasia" val={v(form.nome_fantasia)} />
      </div>
    </Secao>

    <Secao titulo="Dados gerais">
      <div className="fl-rev-grid">
        <Item k="Moeda" val={lbl(MOEDA, form.moeda)} />
        <Item k="Master / FIC" val={v(form.master_fic)} />
        <Item k="Código ANBIMA" val={v(form.codigo_anbima)} />
        <Item k="Constituição/início" val={v(form.data_lancamento)} />
        <Item k="Encerramento" val={v(form.data_encerramento)} />
        <Item k="Voice" val={v(form.voice)} />
        <Item k="Juros" val={lbl(JUROS, form.juros)} />
        <Item k="Ativo" val={sn(form.is_active)} />
        <Item k="Conta no AUM" val={sn(form.count_on_aum)} />
        <Item k="Gera lâmina" val={sn(form.gera_lamina)} />
        <Item k="Paga gestão" val={sn(form.paga_gestao)} />
        <Item k="Observação" val={v(form.observacao)} />
      </div>
    </Secao>

    <Secao titulo="Classificação">
      <div className="fl-rev-grid">
        <Item k="Classificação CVM" val={lbl(FORMA_JURIDICA, form.forma_juridica)} />
        <Item k="ANBIMA nível 1" val={v(form.classe_anbima_n1)} />
        <Item k="ANBIMA nível 2" val={v(form.classe_anbima_n2)} />
        <Item k="ANBIMA nível 3" val={v(form.classe_anbima_n3)} />
        <Item k="ANGA 1" val={v(form.classe_anga_1)} />
        <Item k="ANGA 2" val={v(form.classe_anga_2)} />
      </div>
    </Secao>
  </>
);

// Seção de documentos — exibida no resumo e reutilizada na edição do detalhe.
export const ResumoDocumentos: React.FC<{
  form: FundoForm;
  onDownloadDoc?: (anexo: DocumentoAnexo, versao?: DocumentoVersao) => void;
  onSubstituir?: (anexo: DocumentoAnexo) => void;
}> = ({ form, onDownloadDoc, onSubstituir }) => (
  <Secao titulo="Documentos">
    <table className="fl-table">
      <thead>
        <tr>
          <th>Documento</th><th>Arquivo</th><th>Status</th>
          {(onDownloadDoc || onSubstituir) && <th className="fl-col-acoes"></th>}
        </tr>
      </thead>
      <tbody>
        {DOCS_OBRIGATORIOS.map((doc) => {
          const anexo = form.documentos.find((d) => d.tipo === doc.tipo);
          return (
            <tr key={doc.tipo}>
              <td>{doc.label}</td>
              <td>{anexo?.nome_arquivo || '—'}</td>
              <td><span className={`fl-badge ${anexo ? 'fl-badge--on' : 'fl-badge--off'}`}>{anexo ? 'Anexado' : 'Pendente'}</span></td>
              {(onDownloadDoc || onSubstituir) && (
                <td className="fl-col-acoes">
                  <DocActions
                    anexo={anexo ?? { tipo: doc.tipo, titulo: doc.label }}
                    temArquivo={!!anexo}
                    onDownload={onDownloadDoc}
                    onSubstituir={onSubstituir}
                  />
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  </Secao>
);

// Seções das coleções 1:N do fundo (classes, contas, prestadores, documentos).
// onDownloadDoc/onSubstituir: se fornecidos, mostram o menu de ações (⋯) nos documentos.
export const ResumoColecoes: React.FC<{
  form: FundoForm;
  onDownloadDoc?: (anexo: DocumentoAnexo, versao?: DocumentoVersao) => void;
  onSubstituir?: (anexo: DocumentoAnexo) => void;
}> = ({ form, onDownloadDoc, onSubstituir }) => (
  <>
    <Secao titulo={`Classes / Séries (${form.classes.length})`}>
      <ClassesTabela classes={form.classes} />
    </Secao>

    <Secao titulo={`Contas (${form.contas.length})`}>
      {form.contas.length === 0 ? (
        <div className="fl-empty">Nenhuma conta.</div>
      ) : (
        <table className="fl-table">
          <thead><tr><th>Tipo</th><th>Código</th><th>Banco</th><th>Agência</th><th>Conta</th><th>Favorecido</th></tr></thead>
          <tbody>
            {form.contas.map((c) => (
              <tr key={c._key}>
                <td>{lbl(TIPO_CONTA, c.tipo)}</td><td>{v(c.codigo)}</td><td>{v(c.banco)}</td>
                <td>{v(c.agencia)}</td><td>{v(c.numero_conta)}</td><td>{v(c.favorecido)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Secao>

    <Secao titulo={`Prestadores (${form.prestadores.length})`}>
      {form.prestadores.length === 0 ? (
        <div className="fl-empty">Nenhum prestador.</div>
      ) : (
        <table className="fl-table">
          <thead><tr><th>Prestador</th><th>Papel</th><th>Início</th><th>Fim</th></tr></thead>
          <tbody>
            {form.prestadores.map((p) => (
              <tr key={p._key}>
                <td>{v(p.prestador_nome)}</td>
                <td>{lbl(PAPEL, p.papel)}</td><td>{v(p.data_inicio)}</td><td>{v(p.data_fim)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Secao>

    <ResumoDocumentos form={form} onDownloadDoc={onDownloadDoc} onSubstituir={onSubstituir} />
  </>
);

// Resumo completo = dados do fundo + coleções.
export const ResumoSecoes: React.FC<{
  form: FundoForm;
  onDownloadDoc?: (anexo: DocumentoAnexo, versao?: DocumentoVersao) => void;
  onSubstituir?: (anexo: DocumentoAnexo) => void;
}> = ({ form, onDownloadDoc, onSubstituir }) => (
  <>
    <ResumoDadosFundo form={form} />
    <ResumoColecoes form={form} onDownloadDoc={onDownloadDoc} onSubstituir={onSubstituir} />
  </>
);

const Revisao: React.FC<{ form: FundoForm }> = ({ form }) => (
  <div className="fl-resumo">
    <ResumoSecoes form={form} />
  </div>
);

export default Revisao;
